import { Text, View, ScrollView, Pressable, RefreshControl } from 'react-native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Avatar from '@/components/People/Avatar';
import { markClassCompleted, unmarkClassCompleted, markRequirementCompleted, unmarkRequirementCompleted } from '@/data/training';
import { useNavigation, useRouter, useLocalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivityIndicator } from 'react-native-paper';
import React from 'react';
import PageTitle from '@/components/General/PageTitle';

const getStatusColors = (status) => {
    switch (status) {
        case "ENROLLED": return { bg: "primaryContainer", text: "primary" };
        case "COMPLETED": return { bg: "successContainer", text: "success" };
        case "DROPPED": return { bg: "errorContainer", text: "error" };
        case "UNENROLLED": return { bg: "surfaceVariant", text: "textSecondary" };
        default: return { bg: "surface", text: "onSurface" };
    }
};

export default function StudentProgressScreen() {
    const { moduleID, personID } = useLocalSearchParams();
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: trainingModule, isLoading, isFetching } = Query.trainingModules.byId({ id: moduleID });
    const { data: person } = Query.people.byId({ id: personID });

    const [pendingClassIDs, setPendingClassIDs] = useState(new Set());
    const [pendingReqIDs, setPendingReqIDs] = useState(new Set());

    const invalidateModule = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.trainingModules.byId(moduleID) });
    }, [moduleID]);

    const enrollment = useMemo(() => {
        return trainingModule?.Enrollments?.find(e => e.Person?.id === personID) || null;
    }, [trainingModule, personID]);

    const allClasses = useMemo(() => trainingModule?.Classes || [], [trainingModule]);
    const allClassRequirements = useMemo(() => {
        return allClasses.flatMap(cls => cls.Requirements || []);
    }, [allClasses]);
    const completedClassIDs = useMemo(() => {
        return new Set(enrollment?.ClassCompletions?.map(c => c.classID) || []);
    }, [enrollment]);

    const completedReqIDs = useMemo(() => {
        return new Set(enrollment?.RequirementCompletions?.map(r => r.requirementID) || []);
    }, [enrollment]);

    const nextInstanceByClassID = useMemo(() => {
        const now = new Date();
        const map = new Map();
        for (const cls of allClasses) {
            const upcoming = (cls.Instances || [])
                .filter(i => new Date(i.scheduledAt) >= now)
                .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
            if (upcoming.length > 0) map.set(cls.id, upcoming[0]);
        }
        return map;
    }, [allClasses]);

    const totalItems = allClasses.length + allClassRequirements.length;
    const completedItems = completedClassIDs.size + completedReqIDs.size;
    const percent = totalItems === 0 ? 100 : Math.round((completedItems / totalItems) * 100);

    const statusColors = enrollment ? getStatusColors(enrollment.status) : getStatusColors('');

    useEffect(() => {
        const name = person ? `${person.firstName} ${person.lastName}` : 'Student';
        navigation.setOptions({ 
            headerTitle: () => <PageTitle title={trainingModule?.name ? `${name} — ${trainingModule.name} Progress` : 'Training Progress'} />,
        });
    }, [trainingModule, person]);

    const refresh = useCallback(() => invalidateModule(), [moduleID]);

    const canCheckOff = loggedInUser?.can?.('Manage Training Enrollments') && loggedInUser?.id !== personID;
    const isGraduated = enrollment?.status === 'GRADUATED';

    const markClassMutation = useMutation({
        mutationFn: markClassCompleted,
        onMutate: (vars) => setPendingClassIDs(prev => new Set(prev).add(vars.classID)),
        onSettled: (_, __, vars) => setPendingClassIDs(prev => { const s = new Set(prev); s.delete(vars.classID); return s; }),
        onSuccess: invalidateModule,
    });
    const unmarkClassMutation = useMutation({
        mutationFn: unmarkClassCompleted,
        onMutate: (vars) => setPendingClassIDs(prev => new Set(prev).add(vars.classID)),
        onSettled: (_, __, vars) => setPendingClassIDs(prev => { const s = new Set(prev); s.delete(vars.classID); return s; }),
        onSuccess: invalidateModule,
    });
    const markReqMutation = useMutation({
        mutationFn: markRequirementCompleted,
        onMutate: (vars) => setPendingReqIDs(prev => new Set(prev).add(vars.requirementID)),
        onSettled: (_, __, vars) => setPendingReqIDs(prev => { const s = new Set(prev); s.delete(vars.requirementID); return s; }),
        onSuccess: invalidateModule,
    });
    const unmarkReqMutation = useMutation({
        mutationFn: unmarkRequirementCompleted,
        onMutate: (vars) => setPendingReqIDs(prev => new Set(prev).add(vars.requirementID)),
        onSettled: (_, __, vars) => setPendingReqIDs(prev => { const s = new Set(prev); s.delete(vars.requirementID); return s; }),
        onSuccess: invalidateModule,
    });

    if (isLoading) return <ContentLoader />;
    if (!trainingModule) return <EmptyListPlaceholder icon="school-outline" message="Module not found" />;
    if (!enrollment) return <EmptyListPlaceholder icon="person-outline" message="This person is not enrolled in this module" />;

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.contentContainer}
             keyboardShouldPersistTaps="handled"
            refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh}/>}
        >
            <Animated.View entering={FadeInDown.duration(300)} style={styles.content}>
                {/* Student Header */}
                <View style={styles.headerCard}>
                    <View style={styles.headerTop}>
                        <Pressable onPress={() => router.push('/people/' + personID)} style={styles.personRow}>
                            <Avatar profile={enrollment.Person || person} size={44} />
                            <View style={styles.personInfo}>
                                <Text style={styles.personName}>
                                    {enrollment.Person?.firstName || person?.firstName} {enrollment.Person?.lastName || person?.lastName}
                                </Text>
                                <View style={styles.statusBadge(statusColors.bg)}>
                                    <Text style={styles.statusBadgeText(statusColors.text)}>{enrollment.status}</Text>
                                </View>
                            </View>
                        </Pressable>
                        <Text style={styles.percentText(percent)}>{percent}%</Text>
                    </View>

                    <View style={styles.progressBarBg}>
                        <View style={styles.progressBarFill(percent)} />
                    </View>

                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Unicon name="layers-outline" size={16} colorVariant="primary" />
                            <Text style={styles.statText}>{completedClassIDs.size}/{allClasses.length} classes</Text>
                        </View>
                        <View style={styles.statItem}>
                            <Unicon name="checkmark-circle-outline" size={16} colorVariant="primary" />
                            <Text style={styles.statText}>{completedReqIDs.size}/{allClassRequirements.length} requirements</Text>
                        </View>
                        {enrollment.enrolledAt && (
                            <View style={styles.statItem}>
                                <Unicon name="calendar-outline" size={16} colorVariant="textSecondary" />
                                <Text style={styles.statText}>Enrolled {new Date(enrollment.enrolledAt).toLocaleDateString()}</Text>
                            </View>
                        )}
                    </View>

                    {enrollment.completedAt && (
                        <View style={styles.completedBanner}>
                            <Unicon name="checkmark-done-circle-outline" size={18} colorVariant="success" />
                            <Text style={styles.completedBannerText}>
                                Completed {new Date(enrollment.completedAt).toLocaleDateString()}
                            </Text>
                        </View>
                    )}
                </View>

                {/* Module Info */}
                <View style={styles.moduleInfoCard}>
                    <View style={styles.moduleInfoHeader}>
                        <View style={styles.moduleIcon}>
                            <Unicon name="school-outline" size={22} colorVariant="primary" />
                        </View>
                        <View style={styles.moduleInfoText}>
                            <Pressable onPress={() => router.push('/training/' + moduleID)}>
                                <Text style={styles.moduleName}>{trainingModule.name}</Text>
                            </Pressable>
                            {trainingModule.description ? <Text style={styles.moduleDescription} numberOfLines={3}>{trainingModule.description}</Text> : null}
                        </View>
                    </View>
                    {(trainingModule.Teachers?.length > 0 || trainingModule.Teacher) && (
                        <View style={styles.teacherRow}>
                            <Text style={styles.teacherLabel}>
                                {(trainingModule.Teachers?.length || 1) > 1 ? 'Teachers' : 'Teacher'}
                            </Text>
                            <View style={styles.teachersList}>
                                {(trainingModule.Teachers?.length > 0 ? trainingModule.Teachers : trainingModule.Teacher ? [trainingModule.Teacher] : []).map(t => (
                                    <Pressable key={t.id || t.Person?.id} onPress={() => router.push('/people/' + (t.Person?.id || t.id))} style={styles.teacherChip}>
                                        <Avatar profile={t.Person || t} size={20} disableBorder />
                                        <Text style={styles.teacherChipText}>{(t.Person || t).firstName} {(t.Person || t).lastName}</Text>
                                    </Pressable>
                                ))}
                            </View>
                        </View>
                    )}
                </View>

                {/* Classes Section */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Classes</Text>
                    {allClasses.length === 0
                        ? <Text style={styles.emptyText}>No classes in this module</Text>
                        : allClasses.map((cls, idx) => {
                            const isCompleted = completedClassIDs.has(cls.id);
                            const classReqs = cls.Requirements || [];
                            return (
                                <View key={cls.id} style={styles.classCard}>
                                    <Pressable
                                        style={styles.classCardHeader}
                                        disabled={!canCheckOff || (isGraduated && isCompleted)}
                                        onPress={() => {
                                            if (!canCheckOff || (isGraduated && isCompleted)) return;
                                            if (isCompleted) unmarkClassMutation.mutate({ enrollmentID: enrollment.id, classID: cls.id });
                                            else markClassMutation.mutate({ enrollmentID: enrollment.id, classID: cls.id, markedByID: loggedInUser?.id });
                                        }}
                                    >
                                        <View style={styles.classCardLeft}>
                                            <View style={styles.classCheckCircle(isCompleted)}>
                                                {pendingClassIDs.has(cls.id)
                                                    ? <ActivityIndicator size={16} color={isCompleted ? '#fff' : undefined} />
                                                    : isCompleted
                                                        ? <Unicon name="checkmark" size={16} colorVariant="onPrimary" />
                                                        : <Text style={styles.classNumberText}>{idx + 1}</Text>
                                                }
                                            </View>
                                            <View style={{ flex: 1 }}>
                                                <Text style={styles.className(isCompleted)}>{cls.name}</Text>
                                                {cls.description ? <Text style={styles.classDescription}>{cls.description}</Text> : null}
                                                {!isCompleted && nextInstanceByClassID.has(cls.id) && (() => {
                                                    const inst = nextInstanceByClassID.get(cls.id);
                                                    return (
                                                        <Pressable
                                                            onPress={() => router.push(`/training/${moduleID}/instance/${inst.id}`)}
                                                            style={styles.nextInstanceLink}
                                                        >
                                                            <Unicon name="calendar-outline" size={12} colorVariant="primary" />
                                                            <Text style={styles.nextInstanceText}>
                                                                Next: {new Date(inst.scheduledAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                                            </Text>
                                                        </Pressable>
                                                    );
                                                })()}
                                            </View>
                                        </View>
                                    </Pressable>
                                    {classReqs.length > 0 && (
                                        <View style={styles.classReqList}>
                                            {classReqs.map(req => {
                                                const reqCompleted = completedReqIDs.has(req.id);
                                                return (
                                                    <Pressable
                                                        key={req.id}
                                                        style={styles.reqItem}
                                                        disabled={!canCheckOff || (isGraduated && reqCompleted)}
                                                        onPress={() => {
                                                            if (!canCheckOff || (isGraduated && reqCompleted)) return;
                                                            if (reqCompleted) unmarkReqMutation.mutate({ enrollmentID: enrollment.id, requirementID: req.id });
                                                            else markReqMutation.mutate({ enrollmentID: enrollment.id, requirementID: req.id, markedByID: loggedInUser?.id });
                                                        }}
                                                    >
                                                        {pendingReqIDs.has(req.id)
                                                            ? <ActivityIndicator size={16} />
                                                            : <Unicon
                                                                name={reqCompleted ? "checkmark-circle" : "ellipse-outline"}
                                                                size={16}
                                                                colorVariant={reqCompleted ? "success" : "textSecondary"}
                                                            />
                                                        }
                                                        <Text style={styles.reqItemText(reqCompleted)}>{req.name}</Text>
                                                    </Pressable>
                                                );
                                            })}
                                        </View>
                                    )}
                                </View>
                            );
                        })
                    }
                </View>

                <View style={{ height: 100 }} />
            </Animated.View>
        </ScrollView>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: { flex: 1 },
    contentContainer: { flexGrow: 1 },
    content: { padding: spacing.md, maxWidth: 800, width: '100%', alignSelf: 'center' },

    // Header card
    headerCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: spacing.lg,
        borderWidth: 1,
        borderColor: colors.border,
        marginBottom: spacing.md,
    },
    headerTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: spacing.md,
    },
    personRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        flex: 1,
    },
    personInfo: { flex: 1, gap: 4 },
    personName: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    statusBadge: (bg) => ({
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: 6,
        backgroundColor: colors[bg],
        alignSelf: 'flex-start',
    }),
    statusBadgeText: (text) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors[text],
    }),
    percentText: (percent) => ({
        fontSize: 28,
        fontFamily: fonts.heading,
        color: percent >= 100 ? colors.success : colors.primary,
        marginLeft: spacing.sm,
    }),
    progressBarBg: {
        height: 8,
        backgroundColor: colors.surfaceVariant,
        borderRadius: 4,
        marginBottom: spacing.md,
    },
    progressBarFill: (percent) => ({
        height: 8,
        borderRadius: 4,
        width: `${Math.min(percent, 100)}%`,
        backgroundColor: percent >= 100 ? colors.success : percent >= 50 ? colors.primary : colors.warning,
    }),
    statsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.md,
    },
    statItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    statText: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    completedBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        marginTop: spacing.md,
        paddingTop: spacing.md,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    completedBannerText: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.success,
    },

    // Module info
    moduleInfoCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: spacing.lg,
        borderWidth: 1,
        borderColor: colors.border,
        marginBottom: spacing.lg,
    },
    moduleInfoHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: spacing.sm,
    },
    moduleIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    moduleInfoText: { flex: 1 },
    moduleName: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.primary,
        textDecorationLine: 'underline',
    },
    moduleDescription: {
        fontSize: typography.caption,
        color: colors.textSecondary,
        marginTop: 4,
        lineHeight: 18,
    },
    teacherRow: { marginTop: spacing.md },
    teacherLabel: {
        fontSize: 11,
        color: colors.textSecondary,
        marginBottom: 4,
    },
    teachersList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.sm,
    },
    teacherChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: colors.background,
        borderRadius: 20,
        paddingVertical: 3,
        paddingLeft: 3,
        paddingRight: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
    },
    teacherChipText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.text,
    },

    // Sections
    section: { marginBottom: spacing.lg },
    sectionTitle: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: spacing.sm,
    },
    emptyText: {
        fontSize: typography.caption,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },

    // Class cards
    classCard: {
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: spacing.md,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
    },
    classCardHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
    },
    classCardLeft: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: spacing.sm,
        flex: 1,
    },
    classCheckCircle: (completed) => ({
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: completed ? colors.success : colors.surfaceVariant,
        alignItems: 'center',
        justifyContent: 'center',
    }),
    classNumberText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
    },
    className: (completed) => ({
        fontSize: typography.body,
        fontFamily: fonts.medium,
        color: completed ? colors.success : colors.text,
        textDecorationLine: completed ? 'line-through' : 'none',
    }),
    classDescription: {
        fontSize: typography.caption,
        color: colors.textSecondary,
        marginTop: 2,
    },
    nextInstanceLink: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: 6,
        paddingVertical: 3,
        paddingHorizontal: 8,
        backgroundColor: colors.primaryContainer,
        borderRadius: 6,
        alignSelf: 'flex-start',
        _web: { cursor: 'pointer', transition: 'all 0.15s ease', _hover: { opacity: 0.8 } },
    },
    nextInstanceText: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.primary,
    },
    classReqList: {
        marginTop: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: 6,
    },
    reqItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    reqItemText: (completed) => ({
        fontSize: 13,
        color: completed ? colors.success : colors.text,
        textDecorationLine: completed ? 'line-through' : 'none',
        flex: 1,
    }),

    // Requirement cards
    reqCard: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: spacing.sm,
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: spacing.md,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
    },
    reqCardInfo: { flex: 1 },
    reqCardName: (completed) => ({
        fontSize: typography.body,
        fontFamily: fonts.medium,
        color: completed ? colors.success : colors.text,
        textDecorationLine: completed ? 'line-through' : 'none',
    }),
    reqCardDescription: {
        fontSize: typography.caption,
        color: colors.textSecondary,
        marginTop: 2,
    },
    reqMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        marginTop: 4,
    },
    reqTypeChip: {
        fontSize: 10,
        color: colors.textTertiary,
        backgroundColor: colors.surfaceVariant,
        paddingHorizontal: 6,
        paddingVertical: 1,
        borderRadius: 4,
    },
    reqDueDate: (overdue) => ({
        fontSize: 11,
        color: overdue ? colors.error : colors.textSecondary,
        fontFamily: overdue ? fonts.bold : fonts.regular,
    }),

}));
